import { Module } from '../../types';

export const dspyModule7: Module = {
  id: 'dspy-module7',
  title: 'Production Deployment',
  description:
    'Learn how to deploy DSPy applications to production, including best practices, monitoring, and integration patterns.',
  duration: '8-10 hours',
  isUnderDevelopment: true,
  content: `# Module 7: Production Deployment

## 🎯 Learning Objectives

By the end of this module, you will:
- Understand production considerations for DSPy applications
- Deploy DSPy with FastAPI, MLflow, and Docker
- Implement comprehensive monitoring and observability
- Integrate DSPy with external systems and databases
- Manage production DSPy applications at scale

## 🏭 Production Considerations

### Thread Safety and Concurrency

DSPy modules are thread-safe by default, but there are important considerations:

\`\`\`python
import threading
from concurrent.futures import ThreadPoolExecutor
import asyncio
import dspy

# Thread-safe singleton pattern
class DSPyService:
    _instance = None
    _lock = threading.Lock()
    
    def __new__(cls):
        if cls._instance is None:
            with cls._lock:
                if cls._instance is None:
                    cls._instance = super().__new__(cls)
                    cls._instance.initialize()
        return cls._instance
    
    def initialize(self):
        # Initialize DSPy settings once
        dspy.settings.configure(lm=dspy.OpenAI())
        
        # Create reusable modules
        self.qa_module = dspy.ChainOfThought("question -> answer")
        self.summary_module = dspy.ChainOfThought("text -> summary")
    
    def process_request(self, request_type: str, data: str):
        """Thread-safe request processing."""
        if request_type == "qa":
            return self.qa_module(question=data)
        elif request_type == "summary":
            return self.summary_module(text=data)

# Using with thread pool
service = DSPyService()

def handle_request(request):
    return service.process_request(request["type"], request["data"])

# Thread pool for concurrent requests
with ThreadPoolExecutor(max_workers=10) as executor:
    requests = [
        {"type": "qa", "data": "What is DSPy?"},
        {"type": "summary", "data": "Long text here..."},
    ]
    
    futures = [executor.submit(handle_request, req) for req in requests]
    results = [f.result() for f in futures]
\`\`\`

### Error Handling and Retry Logic

\`\`\`python
import time
from typing import Any, Optional, Callable
from functools import wraps
import logging

logger = logging.getLogger(__name__)

class DSPyProductionError(Exception):
    """Base exception for production DSPy errors."""
    pass

class DSPyRateLimitError(DSPyProductionError):
    """Rate limit exceeded error."""
    pass

class DSPyTimeoutError(DSPyProductionError):
    """Request timeout error."""
    pass

def retry_with_backoff(
    max_retries: int = 3,
    base_delay: float = 1.0,
    max_delay: float = 60.0,
    exponential_base: float = 2.0,
    jitter: bool = True
):
    """Decorator for retry logic with exponential backoff."""
    def decorator(func: Callable) -> Callable:
        @wraps(func)
        def wrapper(*args, **kwargs):
            retry_count = 0
            delay = base_delay
            
            while retry_count < max_retries:
                try:
                    return func(*args, **kwargs)
                except (DSPyRateLimitError, DSPyTimeoutError) as e:
                    retry_count += 1
                    if retry_count >= max_retries:
                        logger.error(f"Max retries exceeded for {func.__name__}")
                        raise
                    
                    # Add jitter to prevent thundering herd
                    if jitter:
                        import random
                        actual_delay = delay * (0.5 + random.random())
                    else:
                        actual_delay = delay
                    
                    logger.warning(
                        f"Retry {retry_count}/{max_retries} for {func.__name__} "
                        f"after {actual_delay:.2f}s delay. Error: {str(e)}"
                    )
                    time.sleep(actual_delay)
                    
                    # Exponential backoff
                    delay = min(delay * exponential_base, max_delay)
                except Exception as e:
                    logger.error(f"Non-retryable error in {func.__name__}: {str(e)}")
                    raise
            
            return None
        return wrapper
    return decorator

# Production-ready DSPy wrapper
class ProductionDSPy:
    def __init__(self, timeout: float = 30.0):
        self.timeout = timeout
        self.modules = {}
        
    @retry_with_backoff(max_retries=3)
    def execute(self, module_name: str, **kwargs) -> Any:
        """Execute DSPy module with production safeguards."""
        try:
            if module_name not in self.modules:
                raise ValueError(f"Unknown module: {module_name}")
            
            # Add timeout handling
            import signal
            
            def timeout_handler(signum, frame):
                raise DSPyTimeoutError(f"Module {module_name} timed out")
            
            # Set timeout
            signal.signal(signal.SIGALRM, timeout_handler)
            signal.alarm(int(self.timeout))
            
            try:
                result = self.modules[module_name](**kwargs)
                signal.alarm(0)  # Clear timeout
                return result
            except Exception as e:
                signal.alarm(0)  # Clear timeout
                if "rate limit" in str(e).lower():
                    raise DSPyRateLimitError(str(e))
                raise
                
        except Exception as e:
            logger.error(f"Error executing {module_name}: {str(e)}")
            raise
\`\`\`

### Resource Management

\`\`\`python
import psutil
import gc
from contextlib import contextmanager
from typing import Dict, Any

class ResourceMonitor:
    """Monitor and manage system resources for DSPy applications."""
    
    def __init__(self, 
                 max_memory_percent: float = 80.0,
                 max_cpu_percent: float = 90.0):
        self.max_memory_percent = max_memory_percent
        self.max_cpu_percent = max_cpu_percent
        self.process = psutil.Process()
        
    def check_resources(self) -> Dict[str, Any]:
        """Check current resource usage."""
        memory_percent = psutil.virtual_memory().percent
        cpu_percent = psutil.cpu_percent(interval=0.1)
        
        return {
            "memory_percent": memory_percent,
            "cpu_percent": cpu_percent,
            "memory_mb": self.process.memory_info().rss / 1024 / 1024,
            "threads": threading.active_count(),
            "healthy": (memory_percent < self.max_memory_percent and 
                       cpu_percent < self.max_cpu_percent)
        }
    
    @contextmanager
    def resource_limit(self):
        """Context manager for resource-limited execution."""
        initial_memory = self.process.memory_info().rss
        
        try:
            yield
        finally:
            # Force garbage collection if memory increased significantly
            current_memory = self.process.memory_info().rss
            if current_memory > initial_memory * 1.5:
                gc.collect()
                logger.info("Forced garbage collection due to memory increase")

# Production caching with memory limits
class BoundedCache:
    """LRU cache with memory bounds."""
    
    def __init__(self, max_size: int = 1000, max_memory_mb: float = 500):
        self.max_size = max_size
        self.max_memory_mb = max_memory_mb
        self.cache = {}
        self.access_times = {}
        self.monitor = ResourceMonitor()
        
    def get(self, key: str) -> Optional[Any]:
        if key in self.cache:
            self.access_times[key] = time.time()
            return self.cache[key]
        return None
    
    def set(self, key: str, value: Any):
        # Check memory before adding
        resources = self.monitor.check_resources()
        if resources["memory_mb"] > self.max_memory_mb:
            self._evict_lru()
        
        # Check size limit
        if len(self.cache) >= self.max_size:
            self._evict_lru()
        
        self.cache[key] = value
        self.access_times[key] = time.time()
    
    def _evict_lru(self):
        """Evict least recently used item."""
        if not self.cache:
            return
        
        lru_key = min(self.access_times, key=self.access_times.get)
        del self.cache[lru_key]
        del self.access_times[lru_key]
\`\`\`

### Cost Management

\`\`\`python
from dataclasses import dataclass
from typing import List, Dict
import json

@dataclass
class TokenUsage:
    prompt_tokens: int
    completion_tokens: int
    total_tokens: int
    model: str
    
    @property
    def estimated_cost(self) -> float:
        """Estimate cost based on model pricing."""
        # Example pricing (update with actual values)
        pricing = {
            "gpt-4": {"prompt": 0.03, "completion": 0.06},  # per 1K tokens
            "gpt-3.5-turbo": {"prompt": 0.0015, "completion": 0.002},
            "claude-3": {"prompt": 0.015, "completion": 0.075},
        }
        
        model_key = self.model.lower()
        for key in pricing:
            if key in model_key:
                rates = pricing[key]
                prompt_cost = (self.prompt_tokens / 1000) * rates["prompt"]
                completion_cost = (self.completion_tokens / 1000) * rates["completion"]
                return prompt_cost + completion_cost
        
        return 0.0

class CostTracker:
    """Track and manage API costs for DSPy applications."""
    
    def __init__(self, budget_limit: float = None, alert_threshold: float = 0.8):
        self.budget_limit = budget_limit
        self.alert_threshold = alert_threshold
        self.usage_history: List[TokenUsage] = []
        self.total_cost = 0.0
        
    def track_usage(self, usage: TokenUsage):
        """Track token usage and cost."""
        self.usage_history.append(usage)
        cost = usage.estimated_cost
        self.total_cost += cost
        
        # Check budget
        if self.budget_limit:
            if self.total_cost >= self.budget_limit:
                raise DSPyProductionError(f"Budget limit exceeded: $" + f"{self.total_cost:.2f}")
            elif self.total_cost >= self.budget_limit * self.alert_threshold:
                logger.warning(
                    f"Approaching budget limit: $" + f"{self.total_cost:.2f} "
                    f"of $" + f"{self.budget_limit:.2f}"
                )
        
        return cost
    
    def get_usage_report(self) -> Dict[str, Any]:
        """Generate usage report."""
        if not self.usage_history:
            return {"total_cost": 0, "total_tokens": 0, "requests": 0}
        
        return {
            "total_cost": self.total_cost,
            "total_tokens": sum(u.total_tokens for u in self.usage_history),
            "requests": len(self.usage_history),
            "average_cost_per_request": self.total_cost / len(self.usage_history),
            "by_model": self._aggregate_by_model(),
        }
    
    def _aggregate_by_model(self) -> Dict[str, Dict[str, float]]:
        """Aggregate usage by model."""
        by_model = {}
        for usage in self.usage_history:
            if usage.model not in by_model:
                by_model[usage.model] = {"cost": 0, "tokens": 0, "requests": 0}
            
            by_model[usage.model]["cost"] += usage.estimated_cost
            by_model[usage.model]["tokens"] += usage.total_tokens
            by_model[usage.model]["requests"] += 1
        
        return by_model
\`\`\`

## 🚀 Deployment Options

### FastAPI Integration

\`\`\`python
from fastapi import FastAPI, HTTPException, BackgroundTasks, Request
from fastapi.middleware.cors import CORSMiddleware
from pydantic import BaseModel, Field
from typing import Optional, Dict, Any
import uvicorn
import dspy

# Request/Response models
class QARequest(BaseModel):
    question: str = Field(..., description="Question to answer")
    context: Optional[str] = Field(None, description="Optional context")
    
class QAResponse(BaseModel):
    answer: str
    confidence: float
    reasoning: Optional[str] = None
    metadata: Dict[str, Any] = {}

class HealthResponse(BaseModel):
    status: str
    version: str
    resources: Dict[str, Any]

# Create FastAPI app
app = FastAPI(
    title="DSPy Production API",
    version="1.0.0",
    description="Production-ready DSPy deployment"
)

# CORS middleware
app.add_middleware(
    CORSMiddleware,
    allow_origins=["*"],  # Configure appropriately for production
    allow_credentials=True,
    allow_methods=["*"],
    allow_headers=["*"],
)

# Global instances
dspy_service = DSPyService()
resource_monitor = ResourceMonitor()
cost_tracker = CostTracker(budget_limit=100.0)  # $100 budget

# Middleware for request tracking
@app.middleware("http")
async def track_requests(request: Request, call_next):
    start_time = time.time()
    
    # Add request ID
    request_id = str(uuid.uuid4())
    request.state.request_id = request_id
    
    # Process request
    response = await call_next(request)
    
    # Log request
    duration = time.time() - start_time
    logger.info(
        f"Request {request_id} completed in {duration:.3f}s: "
        f"{request.method} {request.url.path}"
    )
    
    # Add headers
    response.headers["X-Request-ID"] = request_id
    response.headers["X-Response-Time"] = str(duration)
    
    return response

@app.get("/health", response_model=HealthResponse)
async def health_check():
    """Health check endpoint."""
    resources = resource_monitor.check_resources()
    
    return HealthResponse(
        status="healthy" if resources["healthy"] else "degraded",
        version="1.0.0",
        resources=resources
    )

@app.post("/qa", response_model=QAResponse)
async def question_answering(
    request: QARequest,
    background_tasks: BackgroundTasks
):
    """Question answering endpoint."""
    try:
        # Resource check
        resources = resource_monitor.check_resources()
        if not resources["healthy"]:
            raise HTTPException(
                status_code=503,
                detail="Service temporarily unavailable due to high load"
            )
        
        # Process request
        with resource_monitor.resource_limit():
            result = dspy_service.process_request("qa", request.question)
        
        # Track usage (in background)
        background_tasks.add_task(
            track_api_usage,
            model="gpt-3.5-turbo",
            tokens=100  # Estimate
        )
        
        return QAResponse(
            answer=result.answer,
            confidence=0.95,  # Calculate actual confidence
            reasoning=result.reasoning if hasattr(result, 'reasoning') else None,
            metadata={
                "model": "gpt-3.5-turbo",
                "processing_time": 0.5
            }
        )
        
    except Exception as e:
        logger.error(f"Error in QA endpoint: {str(e)}")
        raise HTTPException(status_code=500, detail=str(e))

def track_api_usage(model: str, tokens: int):
    """Background task to track API usage."""
    usage = TokenUsage(
        prompt_tokens=tokens // 2,
        completion_tokens=tokens // 2,
        total_tokens=tokens,
        model=model
    )
    cost_tracker.track_usage(usage)

@app.get("/metrics")
async def get_metrics():
    """Get service metrics."""
    return {
        "usage": cost_tracker.get_usage_report(),
        "resources": resource_monitor.check_resources(),
    }

# Run with: uvicorn main:app --reload --host 0.0.0.0 --port 8000
if __name__ == "__main__":
    uvicorn.run(
        "main:app",
        host="0.0.0.0",
        port=8000,
        workers=4,  # Multiple workers for production
        log_level="info"
    )
\`\`\`

### MLflow Deployment

\`\`\`python
import mlflow
import mlflow.pyfunc
from mlflow.models.signature import ModelSignature
from mlflow.types.schema import Schema, ColSpec
import pandas as pd

class DSPyMLflowWrapper(mlflow.pyfunc.PythonModel):
    """MLflow wrapper for DSPy models."""
    
    def __init__(self, module_class, module_kwargs=None):
        self.module_class = module_class
        self.module_kwargs = module_kwargs or {}
        
    def load_context(self, context):
        """Load model artifacts and initialize DSPy."""
        import dspy
        
        # Configure DSPy
        dspy.settings.configure(lm=dspy.OpenAI())
        
        # Initialize module
        self.module = self.module_class(**self.module_kwargs)
        
        # Load any additional artifacts
        if "artifacts" in context:
            # Load prompts, examples, etc.
            pass
    
    def predict(self, context, model_input):
        """Make predictions using DSPy module."""
        predictions = []
        
        for _, row in model_input.iterrows():
            try:
                # Convert row to dict and call module
                input_data = row.to_dict()
                result = self.module(**input_data)
                
                # Extract output fields
                output = {}
                for field in result._fields:
                    output[field] = getattr(result, field)
                
                predictions.append(output)
            except Exception as e:
                predictions.append({"error": str(e)})
        
        return pd.DataFrame(predictions)

# Training and logging DSPy model
def train_and_log_dspy_model(train_data: List[dspy.Example]):
    """Train and log DSPy model to MLflow."""
    
    # Start MLflow run
    with mlflow.start_run():
        # Train DSPy module
        class QAModule(dspy.Module):
            def __init__(self):
                self.qa = dspy.ChainOfThought("question -> answer")
            
            def forward(self, question):
                return self.qa(question=question)
        
        # Create and optimize module
        module = QAModule()
        optimizer = dspy.BootstrapFewShotWithRandomSearch(
            metric=lambda example, pred: example.answer in pred.answer,
            max_bootstrapped_demos=4,
            max_labeled_demos=16,
        )
        
        optimized_module = optimizer.compile(
            student=module,
            trainset=train_data
        )
        
        # Define model signature
        input_schema = Schema([
            ColSpec("string", "question")
        ])
        output_schema = Schema([
            ColSpec("string", "answer")
        ])
        signature = ModelSignature(inputs=input_schema, outputs=output_schema)
        
        # Log model
        mlflow.pyfunc.log_model(
            artifact_path="dspy_model",
            python_model=DSPyMLflowWrapper(
                module_class=QAModule,
                module_kwargs={}
            ),
            signature=signature,
            input_example=pd.DataFrame([{"question": "What is DSPy?"}]),
            pip_requirements=[
                "dspy-ai",
                "pandas",
                "openai"
            ]
        )
        
        # Log metrics
        mlflow.log_metric("train_accuracy", 0.95)
        mlflow.log_param("optimizer", "BootstrapFewShotWithRandomSearch")
        mlflow.log_param("max_demos", 16)
        
        # Log artifacts (prompts, examples, etc.)
        mlflow.log_dict(
            {"module_state": optimized_module.dump_state()},
            "module_state.json"
        )

# Serving the model
def serve_mlflow_model(model_uri: str):
    """Serve DSPy model using MLflow."""
    
    # Load model
    model = mlflow.pyfunc.load_model(model_uri)
    
    # Create prediction function
    def predict(question: str) -> str:
        input_df = pd.DataFrame([{"question": question}])
        result_df = model.predict(input_df)
        return result_df.iloc[0]["answer"]
    
    return predict

# Deploy to MLflow Model Registry
def deploy_to_registry(model_name: str, run_id: str):
    """Deploy model to MLflow Model Registry."""
    
    # Register model
    model_uri = f"runs:/{run_id}/dspy_model"
    model_version = mlflow.register_model(model_uri, model_name)
    
    # Transition to production
    client = mlflow.tracking.MlflowClient()
    client.transition_model_version_stage(
        name=model_name,
        version=model_version.version,
        stage="Production"
    )
    
    return model_version
\`\`\`

### Docker Deployment

\`\`\`dockerfile
# Dockerfile for DSPy production deployment
FROM python:3.10-slim

# Install system dependencies
RUN apt-get update && apt-get install -y \\
    gcc \\
    g++ \\
    && rm -rf /var/lib/apt/lists/*

# Set working directory
WORKDIR /app

# Copy requirements first for better caching
COPY requirements.txt .
RUN pip install --no-cache-dir -r requirements.txt

# Copy application code
COPY . .

# Create non-root user
RUN useradd -m -u 1000 dspy && chown -R dspy:dspy /app
USER dspy

# Set environment variables
ENV PYTHONUNBUFFERED=1
ENV DSPY_CACHE_DIR=/app/cache
ENV LOG_LEVEL=INFO

# Health check
HEALTHCHECK --interval=30s --timeout=10s --start-period=40s --retries=3 \\
    CMD python -c "import requests; requests.get('http://localhost:8000/health')"

# Run the application
CMD ["uvicorn", "main:app", "--host", "0.0.0.0", "--port", "8000"]
\`\`\`

\`\`\`yaml
# docker-compose.yml for complete deployment
version: '3.8'

services:
  dspy-api:
    build: .
    ports:
      - "8000:8000"
    environment:
      - OPENAI_API_KEY=\${OPENAI_API_KEY}
      - REDIS_URL=redis://redis:6379
      - POSTGRES_URL=postgresql://user:pass@postgres:5432/dspy
      - LOG_LEVEL=INFO
    depends_on:
      - redis
      - postgres
    volumes:
      - ./cache:/app/cache
      - ./logs:/app/logs
    restart: unless-stopped
    deploy:
      resources:
        limits:
          cpus: '2'
          memory: 4G
        reservations:
          cpus: '1'
          memory: 2G

  redis:
    image: redis:7-alpine
    ports:
      - "6379:6379"
    volumes:
      - redis-data:/data
    restart: unless-stopped

  postgres:
    image: postgres:15-alpine
    environment:
      - POSTGRES_USER=user
      - POSTGRES_PASSWORD=pass
      - POSTGRES_DB=dspy
    ports:
      - "5432:5432"
    volumes:
      - postgres-data:/var/lib/postgresql/data
    restart: unless-stopped

  prometheus:
    image: prom/prometheus:latest
    ports:
      - "9090:9090"
    volumes:
      - ./prometheus.yml:/etc/prometheus/prometheus.yml
      - prometheus-data:/prometheus
    command:
      - '--config.file=/etc/prometheus/prometheus.yml'
      - '--storage.tsdb.path=/prometheus'
    restart: unless-stopped

  grafana:
    image: grafana/grafana:latest
    ports:
      - "3000:3000"
    environment:
      - GF_SECURITY_ADMIN_PASSWORD=admin
    volumes:
      - grafana-data:/var/lib/grafana
      - ./grafana/dashboards:/etc/grafana/provisioning/dashboards
    depends_on:
      - prometheus
    restart: unless-stopped

volumes:
  redis-data:
  postgres-data:
  prometheus-data:
  grafana-data:
\`\`\`

## 📊 Monitoring and Maintenance

### Prometheus Metrics

\`\`\`python
from prometheus_client import Counter, Histogram, Gauge, CollectorRegistry
from prometheus_client import start_http_server, generate_latest
import time

# Create metrics
registry = CollectorRegistry()

# Counters
request_count = Counter(
    'dspy_requests_total',
    'Total number of DSPy requests',
    ['module', 'status'],
    registry=registry
)

token_count = Counter(
    'dspy_tokens_total',
    'Total tokens used',
    ['model', 'token_type'],
    registry=registry
)

# Histograms
request_duration = Histogram(
    'dspy_request_duration_seconds',
    'Request duration in seconds',
    ['module'],
    registry=registry
)

response_size = Histogram(
    'dspy_response_size_bytes',
    'Response size in bytes',
    ['module'],
    registry=registry
)

# Gauges
active_requests = Gauge(
    'dspy_active_requests',
    'Number of active requests',
    registry=registry
)

cache_hit_rate = Gauge(
    'dspy_cache_hit_rate',
    'Cache hit rate',
    registry=registry
)

# Metrics middleware
class MetricsMiddleware:
    def __init__(self, app):
        self.app = app
        
    async def __call__(self, scope, receive, send):
        if scope['type'] == 'http':
            start_time = time.time()
            
            # Track active requests
            active_requests.inc()
            
            try:
                await self.app(scope, receive, send)
                
                # Record metrics
                duration = time.time() - start_time
                request_duration.labels(module='api').observe(duration)
                request_count.labels(module='api', status='success').inc()
                
            except Exception as e:
                request_count.labels(module='api', status='error').inc()
                raise
            finally:
                active_requests.dec()
        else:
            await self.app(scope, receive, send)

# DSPy-specific metrics tracking
def track_dspy_metrics(func):
    """Decorator to track DSPy module metrics."""
    def wrapper(*args, **kwargs):
        module_name = func.__name__
        
        with request_duration.labels(module=module_name).time():
            try:
                result = func(*args, **kwargs)
                request_count.labels(module=module_name, status='success').inc()
                
                # Track token usage if available
                if hasattr(result, '_token_usage'):
                    usage = result._token_usage
                    token_count.labels(
                        model=usage['model'],
                        token_type='prompt'
                    ).inc(usage['prompt_tokens'])
                    token_count.labels(
                        model=usage['model'],
                        token_type='completion'
                    ).inc(usage['completion_tokens'])
                
                return result
                
            except Exception as e:
                request_count.labels(module=module_name, status='error').inc()
                raise
    
    return wrapper

# Expose metrics endpoint
@app.get("/metrics")
async def metrics():
    """Prometheus metrics endpoint."""
    return Response(
        content=generate_latest(registry),
        media_type="text/plain"
    )
\`\`\`

### Logging and Tracing

\`\`\`python
import logging
from opentelemetry import trace
from opentelemetry.exporter.otlp.proto.grpc.trace_exporter import OTLPSpanExporter
from opentelemetry.instrumentation.fastapi import FastAPIInstrumentor
from opentelemetry.sdk.trace import TracerProvider
from opentelemetry.sdk.trace.export import BatchSpanProcessor
from opentelemetry.sdk.resources import Resource
from pythonjsonlogger import jsonlogger
import structlog

# Configure structured logging
def setup_logging():
    """Configure structured JSON logging."""
    
    # Configure Python logging
    logHandler = logging.StreamHandler()
    formatter = jsonlogger.JsonFormatter()
    logHandler.setFormatter(formatter)
    
    logging.basicConfig(
        level=logging.INFO,
        handlers=[logHandler]
    )
    
    # Configure structlog
    structlog.configure(
        processors=[
            structlog.stdlib.filter_by_level,
            structlog.stdlib.add_logger_name,
            structlog.stdlib.add_log_level,
            structlog.stdlib.PositionalArgumentsFormatter(),
            structlog.processors.TimeStamper(fmt="iso"),
            structlog.processors.StackInfoRenderer(),
            structlog.processors.format_exc_info,
            structlog.processors.UnicodeDecoder(),
            structlog.processors.JSONRenderer()
        ],
        context_class=dict,
        logger_factory=structlog.stdlib.LoggerFactory(),
        cache_logger_on_first_use=True,
    )

# Configure OpenTelemetry tracing
def setup_tracing(service_name: str = "dspy-service"):
    """Configure OpenTelemetry tracing."""
    
    # Create resource
    resource = Resource.create({
        "service.name": service_name,
        "service.version": "1.0.0",
    })
    
    # Configure tracer provider
    provider = TracerProvider(resource=resource)
    
    # Configure OTLP exporter
    otlp_exporter = OTLPSpanExporter(
        endpoint="localhost:4317",
        insecure=True
    )
    
    # Add span processor
    span_processor = BatchSpanProcessor(otlp_exporter)
    provider.add_span_processor(span_processor)
    
    # Set global tracer provider
    trace.set_tracer_provider(provider)
    
    # Instrument FastAPI
    FastAPIInstrumentor.instrument_app(app)
    
    return trace.get_tracer(__name__)

# Tracing DSPy operations
tracer = setup_tracing()

def trace_dspy_operation(operation_name: str):
    """Decorator to trace DSPy operations."""
    def decorator(func):
        def wrapper(*args, **kwargs):
            with tracer.start_as_current_span(operation_name) as span:
                # Add attributes
                span.set_attribute("dspy.module", func.__module__)
                span.set_attribute("dspy.function", func.__name__)
                
                try:
                    result = func(*args, **kwargs)
                    span.set_attribute("dspy.success", True)
                    
                    # Add result attributes if available
                    if hasattr(result, '__dict__'):
                        for key, value in result.__dict__.items():
                            if isinstance(value, (str, int, float, bool)):
                                span.set_attribute(f"dspy.result.{key}", value)
                    
                    return result
                    
                except Exception as e:
                    span.set_attribute("dspy.success", False)
                    span.set_attribute("dspy.error", str(e))
                    span.record_exception(e)
                    raise
        
        return wrapper
    return decorator

# Example usage
@trace_dspy_operation("question_answering")
def process_question(question: str) -> str:
    """Process question with tracing."""
    logger = structlog.get_logger()
    
    logger.info(
        "Processing question",
        question=question,
        module="qa"
    )
    
    try:
        result = qa_module(question=question)
        
        logger.info(
            "Question processed successfully",
            question=question,
            answer_length=len(result.answer),
            confidence=0.95
        )
        
        return result.answer
        
    except Exception as e:
        logger.error(
            "Error processing question",
            question=question,
            error=str(e),
            exc_info=True
        )
        raise
\`\`\`

### A/B Testing

\`\`\`python
import random
from typing import Dict, Any, Optional
from datetime import datetime, timedelta
import json

class ABTestManager:
    """Manage A/B tests for DSPy modules."""
    
    def __init__(self, redis_client=None):
        self.redis_client = redis_client
        self.experiments = {}
        
    def create_experiment(
        self,
        name: str,
        variants: Dict[str, Any],
        traffic_split: Dict[str, float],
        duration_days: int = 7
    ):
        """Create a new A/B test experiment."""
        if sum(traffic_split.values()) != 1.0:
            raise ValueError("Traffic split must sum to 1.0")
        
        experiment = {
            "name": name,
            "variants": variants,
            "traffic_split": traffic_split,
            "start_time": datetime.utcnow().isoformat(),
            "end_time": (datetime.utcnow() + timedelta(days=duration_days)).isoformat(),
            "metrics": {variant: {"count": 0, "success": 0} for variant in variants}
        }
        
        self.experiments[name] = experiment
        
        # Store in Redis if available
        if self.redis_client:
            self.redis_client.set(
                f"experiment:{name}",
                json.dumps(experiment),
                ex=duration_days * 24 * 3600
            )
    
    def get_variant(self, experiment_name: str, user_id: str) -> Optional[str]:
        """Get variant for user in experiment."""
        if experiment_name not in self.experiments:
            return None
        
        experiment = self.experiments[experiment_name]
        
        # Check if experiment is active
        if datetime.utcnow() > datetime.fromisoformat(experiment["end_time"]):
            return None
        
        # Consistent hashing for user assignment
        hash_value = hash(f"{experiment_name}:{user_id}") % 100 / 100.0
        
        cumulative = 0.0
        for variant, split in experiment["traffic_split"].items():
            cumulative += split
            if hash_value < cumulative:
                return variant
        
        return list(experiment["variants"].keys())[0]
    
    def track_result(
        self,
        experiment_name: str,
        variant: str,
        success: bool,
        metadata: Dict[str, Any] = None
    ):
        """Track result for experiment variant."""
        if experiment_name not in self.experiments:
            return
        
        metrics = self.experiments[experiment_name]["metrics"][variant]
        metrics["count"] += 1
        if success:
            metrics["success"] += 1
        
        # Store detailed result if Redis available
        if self.redis_client and metadata:
            result = {
                "experiment": experiment_name,
                "variant": variant,
                "success": success,
                "timestamp": datetime.utcnow().isoformat(),
                **metadata
            }
            
            self.redis_client.lpush(
                f"experiment_results:{experiment_name}",
                json.dumps(result)
            )
    
    def get_results(self, experiment_name: str) -> Dict[str, Any]:
        """Get experiment results with statistical significance."""
        if experiment_name not in self.experiments:
            return {}
        
        experiment = self.experiments[experiment_name]
        results = {}
        
        for variant, metrics in experiment["metrics"].items():
            if metrics["count"] > 0:
                success_rate = metrics["success"] / metrics["count"]
                results[variant] = {
                    "success_rate": success_rate,
                    "count": metrics["count"],
                    "confidence_interval": self._calculate_confidence_interval(
                        success_rate,
                        metrics["count"]
                    )
                }
        
        # Calculate statistical significance if two variants
        if len(results) == 2:
            variants = list(results.keys())
            p_value = self._calculate_p_value(
                results[variants[0]]["success_rate"],
                results[variants[0]]["count"],
                results[variants[1]]["success_rate"],
                results[variants[1]]["count"]
            )
            results["p_value"] = p_value
            results["significant"] = p_value < 0.05
        
        return results
    
    def _calculate_confidence_interval(
        self,
        success_rate: float,
        sample_size: int,
        confidence: float = 0.95
    ) -> tuple:
        """Calculate confidence interval for success rate."""
        import scipy.stats as stats
        
        z = stats.norm.ppf((1 + confidence) / 2)
        margin = z * ((success_rate * (1 - success_rate)) / sample_size) ** 0.5
        
        return (
            max(0, success_rate - margin),
            min(1, success_rate + margin)
        )
    
    def _calculate_p_value(
        self,
        rate1: float,
        n1: int,
        rate2: float,
        n2: int
    ) -> float:
        """Calculate p-value for difference in success rates."""
        import scipy.stats as stats
        
        # Pooled success rate
        p_pool = (rate1 * n1 + rate2 * n2) / (n1 + n2)
        
        # Standard error
        se = (p_pool * (1 - p_pool) * (1/n1 + 1/n2)) ** 0.5
        
        # Z-score
        z = (rate1 - rate2) / se
        
        # Two-tailed p-value
        return 2 * (1 - stats.norm.cdf(abs(z)))

# Using A/B testing with DSPy
ab_manager = ABTestManager(redis_client=redis.Redis())

# Create experiment
ab_manager.create_experiment(
    name="qa_prompt_optimization",
    variants={
        "control": dspy.ChainOfThought("question -> answer"),
        "variant_a": dspy.ChainOfThought(
            "question -> reasoning -> answer"
        ),
    },
    traffic_split={"control": 0.5, "variant_a": 0.5},
    duration_days=7
)

@app.post("/qa_with_ab")
async def qa_with_ab_testing(request: QARequest, user_id: str):
    """QA endpoint with A/B testing."""
    
    # Get variant for user
    variant = ab_manager.get_variant("qa_prompt_optimization", user_id)
    
    if not variant:
        # Fallback to default
        module = dspy_service.qa_module
    else:
        module = ab_manager.experiments["qa_prompt_optimization"]["variants"][variant]
    
    # Process request
    try:
        result = module(question=request.question)
        
        # Track success (define your success metric)
        success = len(result.answer) > 10  # Example metric
        ab_manager.track_result(
            "qa_prompt_optimization",
            variant,
            success,
            metadata={
                "user_id": user_id,
                "answer_length": len(result.answer)
            }
        )
        
        return {
            "answer": result.answer,
            "variant": variant,  # Include for debugging
        }
        
    except Exception as e:
        ab_manager.track_result(
            "qa_prompt_optimization",
            variant,
            False,
            metadata={"error": str(e)}
        )
        raise
\`\`\`

### Continuous Learning

\`\`\`python
from typing import List, Tuple
import numpy as np
from collections import deque

class ContinuousLearningPipeline:
    """Pipeline for continuous learning and improvement."""
    
    def __init__(
        self,
        base_module: dspy.Module,
        feedback_threshold: float = 0.8,
        retraining_interval: int = 100
    ):
        self.base_module = base_module
        self.feedback_threshold = feedback_threshold
        self.retraining_interval = retraining_interval
        
        self.feedback_buffer = deque(maxlen=1000)
        self.interaction_count = 0
        self.last_retrain = 0
        
    def process_with_feedback(
        self,
        input_data: Dict[str, Any],
        get_feedback: bool = True
    ) -> Tuple[Any, Optional[float]]:
        """Process input and optionally collect feedback."""
        
        # Get prediction
        result = self.base_module(**input_data)
        
        # Collect feedback if enabled
        feedback_score = None
        if get_feedback:
            feedback_score = self._collect_feedback(input_data, result)
            
            # Store feedback
            self.feedback_buffer.append({
                "input": input_data,
                "output": result,
                "feedback": feedback_score,
                "timestamp": datetime.utcnow()
            })
        
        self.interaction_count += 1
        
        # Check if retraining needed
        if self._should_retrain():
            self._retrain_module()
        
        return result, feedback_score
    
    def _collect_feedback(
        self,
        input_data: Dict[str, Any],
        output: Any
    ) -> float:
        """Collect feedback on the output."""
        # This could be:
        # 1. Implicit feedback (user engagement metrics)
        # 2. Explicit feedback (thumbs up/down)
        # 3. Automated quality checks
        
        # Example: automated quality check
        quality_score = self._automated_quality_check(output)
        
        return quality_score
    
    def _automated_quality_check(self, output: Any) -> float:
        """Perform automated quality checks."""
        score = 1.0
        
        # Check output length
        if hasattr(output, 'answer'):
            answer = output.answer
            if len(answer) < 10:
                score *= 0.5
            if len(answer) > 1000:
                score *= 0.8
        
        # Check for common issues
        if any(phrase in str(output).lower() for phrase in [
            "i don't know",
            "i'm not sure",
            "error",
            "undefined"
        ]):
            score *= 0.7
        
        return score
    
    def _should_retrain(self) -> bool:
        """Determine if retraining is needed."""
        if self.interaction_count - self.last_retrain < self.retraining_interval:
            return False
        
        # Check recent performance
        recent_feedback = list(self.feedback_buffer)[-50:]
        if not recent_feedback:
            return False
        
        avg_score = np.mean([f["feedback"] for f in recent_feedback])
        
        return avg_score < self.feedback_threshold
    
    def _retrain_module(self):
        """Retrain the module with recent data."""
        logger.info("Starting module retraining")
        
        # Convert feedback to training examples
        train_examples = []
        for item in self.feedback_buffer:
            if item["feedback"] > self.feedback_threshold:
                example = dspy.Example(
                    **item["input"],
                    **{k: v for k, v in item["output"].__dict__.items() 
                       if not k.startswith('_')}
                )
                train_examples.append(example)
        
        if len(train_examples) < 10:
            logger.warning("Insufficient high-quality examples for retraining")
            return
        
        # Retrain with bootstrap few-shot
        optimizer = dspy.BootstrapFewShot(metric=None)
        
        try:
            self.base_module = optimizer.compile(
                self.base_module,
                trainset=train_examples[-100:]  # Use recent examples
            )
            
            self.last_retrain = self.interaction_count
            logger.info(f"Retraining completed with {len(train_examples)} examples")
            
        except Exception as e:
            logger.error(f"Retraining failed: {str(e)}")

# Integration with production API
continuous_learner = ContinuousLearningPipeline(
    base_module=dspy_service.qa_module,
    feedback_threshold=0.8,
    retraining_interval=100
)

@app.post("/qa_with_learning")
async def qa_with_continuous_learning(
    request: QARequest,
    collect_feedback: bool = True
):
    """QA endpoint with continuous learning."""
    
    result, feedback_score = continuous_learner.process_with_feedback(
        {"question": request.question},
        get_feedback=collect_feedback
    )
    
    return {
        "answer": result.answer,
        "feedback_score": feedback_score,
        "learning_enabled": True
    }

@app.post("/feedback/{request_id}")
async def submit_feedback(
    request_id: str,
    feedback: float = Field(..., ge=0, le=1)
):
    """Submit explicit feedback for a request."""
    # Store feedback for future retraining
    # Implementation depends on your storage solution
    pass
\`\`\`

## 🔌 Integration Patterns

### API Gateway Integration

\`\`\`python
from typing import Dict, Any, Optional
import httpx
from tenacity import retry, stop_after_attempt, wait_exponential

class DSPyAPIClient:
    """Client for integrating DSPy with API gateways."""
    
    def __init__(
        self,
        base_url: str,
        api_key: Optional[str] = None,
        timeout: float = 30.0
    ):
        self.base_url = base_url.rstrip('/')
        self.timeout = timeout
        
        # Configure headers
        self.headers = {"Content-Type": "application/json"}
        if api_key:
            self.headers["Authorization"] = f"Bearer {api_key}"
        
        # Create HTTP client
        self.client = httpx.AsyncClient(
            headers=self.headers,
            timeout=timeout
        )
    
    @retry(
        stop=stop_after_attempt(3),
        wait=wait_exponential(multiplier=1, min=4, max=10)
    )
    async def call_dspy_endpoint(
        self,
        endpoint: str,
        data: Dict[str, Any]
    ) -> Dict[str, Any]:
        """Call DSPy endpoint with retry logic."""
        url = f"{self.base_url}/{endpoint}"
        
        try:
            response = await self.client.post(url, json=data)
            response.raise_for_status()
            return response.json()
            
        except httpx.HTTPStatusError as e:
            if e.response.status_code == 429:
                # Rate limit - retry will handle
                raise
            elif e.response.status_code >= 500:
                # Server error - retry will handle
                raise
            else:
                # Client error - don't retry
                raise ValueError(f"API error: {e.response.text}")
        
        except httpx.RequestError as e:
            # Network error - retry will handle
            raise
    
    async def batch_process(
        self,
        endpoint: str,
        items: List[Dict[str, Any]],
        batch_size: int = 10
    ) -> List[Dict[str, Any]]:
        """Process items in batches."""
        results = []
        
        for i in range(0, len(items), batch_size):
            batch = items[i:i + batch_size]
            
            # Process batch concurrently
            tasks = [
                self.call_dspy_endpoint(endpoint, item)
                for item in batch
            ]
            
            batch_results = await asyncio.gather(*tasks, return_exceptions=True)
            
            # Handle results
            for item, result in zip(batch, batch_results):
                if isinstance(result, Exception):
                    results.append({
                        "error": str(result),
                        "input": item
                    })
                else:
                    results.append(result)
        
        return results
    
    async def close(self):
        """Close the HTTP client."""
        await self.client.aclose()

# Kong/AWS API Gateway integration example
class APIGatewayIntegration:
    """Integration with API Gateway for DSPy services."""
    
    def __init__(self, gateway_url: str, api_key: str):
        self.gateway_url = gateway_url
        self.api_key = api_key
        
    def create_lambda_handler(self, dspy_module: dspy.Module):
        """Create AWS Lambda handler for DSPy module."""
        
        def lambda_handler(event, context):
            """AWS Lambda handler."""
            try:
                # Parse request
                body = json.loads(event.get('body', '{}'))
                
                # Process with DSPy
                result = dspy_module(**body)
                
                # Format response
                response_body = {
                    k: v for k, v in result.__dict__.items()
                    if not k.startswith('_')
                }
                
                return {
                    'statusCode': 200,
                    'headers': {
                        'Content-Type': 'application/json',
                        'X-Request-ID': context.request_id
                    },
                    'body': json.dumps(response_body)
                }
                
            except Exception as e:
                return {
                    'statusCode': 500,
                    'headers': {'Content-Type': 'application/json'},
                    'body': json.dumps({'error': str(e)})
                }
        
        return lambda_handler
\`\`\`

### Message Queue Integration

\`\`\`python
import asyncio
from aiokafka import AIOKafkaConsumer, AIOKafkaProducer
import json
from typing import Optional

class DSPyKafkaProcessor:
    """Process DSPy requests via Kafka."""
    
    def __init__(
        self,
        dspy_modules: Dict[str, dspy.Module],
        kafka_config: Dict[str, Any]
    ):
        self.modules = dspy_modules
        self.kafka_config = kafka_config
        self.consumer: Optional[AIOKafkaConsumer] = None
        self.producer: Optional[AIOKafkaProducer] = None
        
    async def start(self):
        """Start Kafka consumer and producer."""
        self.consumer = AIOKafkaConsumer(
            'dspy-requests',
            bootstrap_servers=self.kafka_config['bootstrap_servers'],
            group_id='dspy-processors',
            value_deserializer=lambda m: json.loads(m.decode('utf-8'))
        )
        
        self.producer = AIOKafkaProducer(
            bootstrap_servers=self.kafka_config['bootstrap_servers'],
            value_serializer=lambda v: json.dumps(v).encode('utf-8')
        )
        
        await self.consumer.start()
        await self.producer.start()
    
    async def process_messages(self):
        """Process messages from Kafka."""
        async for msg in self.consumer:
            try:
                # Extract request
                request = msg.value
                module_name = request.get('module')
                request_id = request.get('request_id')
                data = request.get('data', {})
                
                # Process with DSPy
                if module_name not in self.modules:
                    raise ValueError(f"Unknown module: {module_name}")
                
                result = self.modules[module_name](**data)
                
                # Send response
                response = {
                    'request_id': request_id,
                    'status': 'success',
                    'result': {
                        k: v for k, v in result.__dict__.items()
                        if not k.startswith('_')
                    }
                }
                
                await self.producer.send(
                    'dspy-responses',
                    key=request_id.encode('utf-8'),
                    value=response
                )
                
            except Exception as e:
                # Send error response
                error_response = {
                    'request_id': request.get('request_id', 'unknown'),
                    'status': 'error',
                    'error': str(e)
                }
                
                await self.producer.send(
                    'dspy-responses',
                    value=error_response
                )
                
                logger.error(f"Error processing message: {str(e)}")
    
    async def stop(self):
        """Stop Kafka consumer and producer."""
        if self.consumer:
            await self.consumer.stop()
        if self.producer:
            await self.producer.stop()

# Celery integration for async processing
from celery import Celery

celery_app = Celery('dspy_tasks', broker='redis://localhost:6379')

@celery_app.task(bind=True, max_retries=3)
def process_dspy_task(self, module_name: str, input_data: Dict[str, Any]):
    """Celery task for DSPy processing."""
    try:
        # Initialize DSPy module
        if module_name == "qa":
            module = dspy.ChainOfThought("question -> answer")
        elif module_name == "summary":
            module = dspy.ChainOfThought("text -> summary")
        else:
            raise ValueError(f"Unknown module: {module_name}")
        
        # Process
        result = module(**input_data)
        
        return {
            'status': 'success',
            'result': result.__dict__
        }
        
    except Exception as e:
        # Retry with exponential backoff
        raise self.retry(exc=e, countdown=2 ** self.request.retries)
\`\`\`

### Database Integration

\`\`\`python
from sqlalchemy import create_engine, Column, String, Float, DateTime, JSON
from sqlalchemy.ext.declarative import declarative_base
from sqlalchemy.orm import sessionmaker
import uuid

Base = declarative_base()

class DSPyRequest(Base):
    """Database model for DSPy requests."""
    __tablename__ = 'dspy_requests'
    
    id = Column(String, primary_key=True, default=lambda: str(uuid.uuid4()))
    module = Column(String, nullable=False)
    input_data = Column(JSON, nullable=False)
    output_data = Column(JSON)
    status = Column(String, default='pending')
    created_at = Column(DateTime, default=datetime.utcnow)
    completed_at = Column(DateTime)
    error = Column(String)
    metadata = Column(JSON)

class DSPyDatabaseIntegration:
    """Database integration for DSPy."""
    
    def __init__(self, database_url: str):
        self.engine = create_engine(database_url)
        Base.metadata.create_all(self.engine)
        self.Session = sessionmaker(bind=self.engine)
    
    async def store_request(
        self,
        module: str,
        input_data: Dict[str, Any],
        metadata: Optional[Dict[str, Any]] = None
    ) -> str:
        """Store DSPy request in database."""
        session = self.Session()
        
        try:
            request = DSPyRequest(
                module=module,
                input_data=input_data,
                metadata=metadata or {}
            )
            
            session.add(request)
            session.commit()
            
            return request.id
            
        finally:
            session.close()
    
    async def update_request(
        self,
        request_id: str,
        output_data: Optional[Dict[str, Any]] = None,
        error: Optional[str] = None
    ):
        """Update request with results."""
        session = self.Session()
        
        try:
            request = session.query(DSPyRequest).filter_by(id=request_id).first()
            
            if not request:
                raise ValueError(f"Request {request_id} not found")
            
            if output_data:
                request.output_data = output_data
                request.status = 'completed'
            elif error:
                request.error = error
                request.status = 'failed'
            
            request.completed_at = datetime.utcnow()
            session.commit()
            
        finally:
            session.close()
    
    async def get_request_history(
        self,
        module: Optional[str] = None,
        limit: int = 100
    ) -> List[Dict[str, Any]]:
        """Get request history from database."""
        session = self.Session()
        
        try:
            query = session.query(DSPyRequest)
            
            if module:
                query = query.filter_by(module=module)
            
            requests = query.order_by(
                DSPyRequest.created_at.desc()
            ).limit(limit).all()
            
            return [
                {
                    'id': req.id,
                    'module': req.module,
                    'input': req.input_data,
                    'output': req.output_data,
                    'status': req.status,
                    'created_at': req.created_at.isoformat(),
                    'error': req.error
                }
                for req in requests
            ]
            
        finally:
            session.close()

# Vector database integration for semantic search
class DSPyVectorStore:
    """Vector store integration for DSPy."""
    
    def __init__(self, collection_name: str = "dspy_embeddings"):
        import chromadb
        
        self.client = chromadb.Client()
        self.collection = self.client.get_or_create_collection(
            name=collection_name
        )
    
    async def store_interaction(
        self,
        interaction_id: str,
        input_text: str,
        output_text: str,
        metadata: Dict[str, Any] = None
    ):
        """Store DSPy interaction with embeddings."""
        # Combine input and output for embedding
        combined_text = f"Input: {input_text}\\nOutput: {output_text}"
        
        self.collection.add(
            documents=[combined_text],
            ids=[interaction_id],
            metadatas=[metadata or {}]
        )
    
    async def find_similar(
        self,
        query: str,
        n_results: int = 5
    ) -> List[Dict[str, Any]]:
        """Find similar interactions."""
        results = self.collection.query(
            query_texts=[query],
            n_results=n_results
        )
        
        return [
            {
                'id': results['ids'][0][i],
                'document': results['documents'][0][i],
                'metadata': results['metadatas'][0][i],
                'distance': results['distances'][0][i]
            }
            for i in range(len(results['ids'][0]))
        ]
\`\`\`

## 📋 Module Summary

This module covered comprehensive production deployment strategies for DSPy applications, including:

1. **Production Considerations**: Thread safety, error handling, resource management, and cost optimization
2. **Deployment Options**: FastAPI integration, MLflow deployment, and Docker containerization
3. **Monitoring and Maintenance**: Metrics collection, logging, tracing, A/B testing, and continuous learning
4. **Integration Patterns**: API gateways, message queues, and database integrations

You now have the knowledge to deploy and maintain DSPy applications in production environments with confidence.`,
  exercises: [
    {
      id: 'dspy-m7-ex1',
      title: 'Production API Implementation',
      description: 'Build a production-ready FastAPI service with monitoring and error handling',
      difficulty: 'expert',
      type: 'code',
      instructions:
        'Create a complete production API with health checks, metrics, and proper error handling.',
      hints: ['Implement retry logic', 'Add resource monitoring', 'Include structured logging'],
      solution: `from fastapi import FastAPI, HTTPException
from prometheus_client import Counter, Histogram
import structlog

logger = structlog.get_logger()

app = FastAPI(title="Production DSPy API")

# Metrics
request_counter = Counter('dspy_requests', 'Total requests', ['endpoint', 'status'])
latency_histogram = Histogram('dspy_latency', 'Request latency')

class ProductionService:
    def __init__(self):
        self.qa_module = dspy.ChainOfThought("question -> answer")
        self.resource_monitor = ResourceMonitor()
    
    @retry_with_backoff(max_retries=3)
    async def process_qa(self, question: str):
        with latency_histogram.time():
            resources = self.resource_monitor.check_resources()
            if not resources["healthy"]:
                raise HTTPException(503, "Service degraded")
            
            try:
                result = self.qa_module(question=question)
                request_counter.labels(endpoint='qa', status='success').inc()
                return result
            except Exception as e:
                request_counter.labels(endpoint='qa', status='error').inc()
                logger.error("QA processing error", error=str(e))
                raise

service = ProductionService()

@app.post("/qa")
async def qa_endpoint(request: QARequest):
    result = await service.process_qa(request.question)
    return {"answer": result.answer}`,
      testCases: [
        {
          input: 'Production API with monitoring and error handling',
          expected: true,
          description: 'Complete production service implementation',
        },
      ],
    },
    {
      id: 'dspy-m7-ex2',
      title: 'Docker Deployment Setup',
      description: 'Create a complete Docker deployment with all supporting services',
      difficulty: 'hard',
      type: 'code',
      instructions: 'Build a Docker Compose setup with DSPy API, Redis, Postgres, and monitoring.',
      hints: ['Include health checks', 'Configure resource limits', 'Set up persistent volumes'],
      solution: `# docker-compose.yml
version: '3.8'

services:
  dspy-api:
    build: 
      context: .
      dockerfile: Dockerfile
    ports:
      - "8000:8000"
    environment:
      - OPENAI_API_KEY=\${OPENAI_API_KEY}
      - DATABASE_URL=postgresql://user:pass@postgres:5432/dspy
      - REDIS_URL=redis://redis:6379
    depends_on:
      postgres:
        condition: service_healthy
      redis:
        condition: service_healthy
    healthcheck:
      test: ["CMD", "curl", "-f", "http://localhost:8000/health"]
      interval: 30s
      timeout: 10s
      retries: 3
    deploy:
      resources:
        limits:
          cpus: '2'
          memory: 4G
    volumes:
      - ./logs:/app/logs
      - dspy-cache:/app/cache

  postgres:
    image: postgres:15
    environment:
      POSTGRES_USER: user
      POSTGRES_PASSWORD: pass
      POSTGRES_DB: dspy
    volumes:
      - postgres-data:/var/lib/postgresql/data
    healthcheck:
      test: ["CMD-SHELL", "pg_isready -U user"]
      interval: 10s
      timeout: 5s
      retries: 5

  redis:
    image: redis:7-alpine
    volumes:
      - redis-data:/data
    healthcheck:
      test: ["CMD", "redis-cli", "ping"]
      interval: 10s
      timeout: 5s
      retries: 5

  prometheus:
    image: prom/prometheus
    ports:
      - "9090:9090"
    volumes:
      - ./prometheus.yml:/etc/prometheus/prometheus.yml
      - prometheus-data:/prometheus

volumes:
  postgres-data:
  redis-data:
  prometheus-data:
  dspy-cache:`,
      testCases: [
        {
          input: 'Complete Docker Compose configuration',
          expected: true,
          description: 'Full production deployment setup',
        },
      ],
    },
    {
      id: 'dspy-m7-ex3',
      title: 'Monitoring and Alerting',
      description: 'Implement comprehensive monitoring with Prometheus and alerting',
      difficulty: 'hard',
      type: 'code',
      instructions: 'Create monitoring setup with custom metrics, dashboards, and alerts.',
      hints: ['Track key metrics', 'Set up alert rules', 'Create Grafana dashboards'],
      solution: `# Monitoring implementation
from prometheus_client import Counter, Histogram, Gauge, Info
import time

class DSPyMonitoring:
    def __init__(self):
        # Metrics
        self.request_count = Counter(
            'dspy_requests_total',
            'Total requests',
            ['module', 'status']
        )
        
        self.request_duration = Histogram(
            'dspy_request_duration_seconds',
            'Request duration',
            ['module'],
            buckets=(0.1, 0.5, 1.0, 2.0, 5.0, 10.0)
        )
        
        self.token_usage = Counter(
            'dspy_tokens_total',
            'Total tokens used',
            ['model', 'type']
        )
        
        self.active_requests = Gauge(
            'dspy_active_requests',
            'Active requests'
        )
        
        self.model_info = Info(
            'dspy_model',
            'Model information'
        )
        
        self.model_info.info({
            'model': 'gpt-3.5-turbo',
            'version': '1.0.0'
        })
    
    def track_request(self, module: str):
        """Context manager for tracking requests."""
        class RequestTracker:
            def __init__(self, monitoring, module):
                self.monitoring = monitoring
                self.module = module
                self.start_time = None
                
            def __enter__(self):
                self.monitoring.active_requests.inc()
                self.start_time = time.time()
                return self
                
            def __exit__(self, exc_type, exc_val, exc_tb):
                duration = time.time() - self.start_time
                self.monitoring.active_requests.dec()
                
                status = 'error' if exc_type else 'success'
                self.monitoring.request_count.labels(
                    module=self.module,
                    status=status
                ).inc()
                
                self.monitoring.request_duration.labels(
                    module=self.module
                ).observe(duration)
        
        return RequestTracker(self, module)

# Prometheus alert rules (prometheus-rules.yml)
alert_rules = """
groups:
  - name: dspy_alerts
    interval: 30s
    rules:
      - alert: HighErrorRate
        expr: rate(dspy_requests_total{status="error"}[5m]) > 0.1
        for: 5m
        labels:
          severity: critical
        annotations:
          summary: High error rate detected
          description: "Error rate is {{ $value }} errors per second"
      
      - alert: HighLatency
        expr: histogram_quantile(0.95, rate(dspy_request_duration_seconds_bucket[5m])) > 5
        for: 5m
        labels:
          severity: warning
        annotations:
          summary: High request latency
          description: "95th percentile latency is {{ $value }} seconds"
      
      - alert: HighTokenUsage
        expr: rate(dspy_tokens_total[1h]) > 10000
        for: 10m
        labels:
          severity: warning
        annotations:
          summary: High token usage rate
          description: "Token usage rate is {{ $value }} tokens per hour"
"""

# Grafana dashboard configuration
grafana_dashboard = {
    "dashboard": {
        "title": "DSPy Production Metrics",
        "panels": [
            {
                "title": "Request Rate",
                "targets": [{
                    "expr": "rate(dspy_requests_total[5m])"
                }]
            },
            {
                "title": "Error Rate",
                "targets": [{
                    "expr": "rate(dspy_requests_total{status='error'}[5m])"
                }]
            },
            {
                "title": "Latency (p50, p95, p99)",
                "targets": [
                    {"expr": "histogram_quantile(0.5, rate(dspy_request_duration_seconds_bucket[5m]))"},
                    {"expr": "histogram_quantile(0.95, rate(dspy_request_duration_seconds_bucket[5m]))"},
                    {"expr": "histogram_quantile(0.99, rate(dspy_request_duration_seconds_bucket[5m]))"}
                ]
            },
            {
                "title": "Token Usage",
                "targets": [{
                    "expr": "rate(dspy_tokens_total[5m])"
                }]
            }
        ]
    }
}`,
      testCases: [
        {
          input: 'Comprehensive monitoring with metrics and alerts',
          expected: true,
          description: 'Complete monitoring implementation',
        },
      ],
    },
    {
      id: 'dspy-m7-ex4',
      title: 'Continuous Learning Pipeline',
      description: 'Build a continuous learning system that improves from user feedback',
      difficulty: 'expert',
      type: 'code',
      instructions: 'Implement a system that collects feedback and automatically retrains.',
      hints: ['Store feedback data', 'Implement quality checks', 'Schedule retraining'],
      solution: `class ContinuousLearningSystem:
    def __init__(self):
        self.base_module = dspy.ChainOfThought("question -> answer")
        self.feedback_store = FeedbackStore()
        self.quality_threshold = 0.8
        self.retrain_schedule = "0 2 * * *"  # 2 AM daily
        
    async def process_with_learning(self, question: str, user_id: str):
        # Generate response
        response = self.base_module(question=question)
        
        # Create feedback entry
        feedback_id = await self.feedback_store.create_entry({
            "question": question,
            "answer": response.answer,
            "user_id": user_id,
            "timestamp": datetime.utcnow()
        })
        
        # Automated quality check
        quality_score = self.automated_quality_check(response)
        
        if quality_score < self.quality_threshold:
            await self.flag_for_review(feedback_id)
        
        return {
            "answer": response.answer,
            "feedback_id": feedback_id,
            "quality_score": quality_score
        }
    
    def automated_quality_check(self, response):
        score = 1.0
        
        # Length checks
        if len(response.answer) < 20:
            score *= 0.7
        
        # Confidence checks
        uncertain_phrases = ["might be", "possibly", "not sure"]
        if any(phrase in response.answer.lower() for phrase in uncertain_phrases):
            score *= 0.8
        
        # Relevance check (simplified)
        if hasattr(response, 'reasoning'):
            if len(response.reasoning) > len(response.answer) * 2:
                score *= 0.9
        
        return score
    
    async def collect_feedback(self, feedback_id: str, rating: float):
        await self.feedback_store.update_rating(feedback_id, rating)
        
        # Trigger retraining if needed
        recent_ratings = await self.feedback_store.get_recent_ratings(hours=24)
        avg_rating = sum(recent_ratings) / len(recent_ratings)
        
        if avg_rating < self.quality_threshold:
            await self.trigger_retrain()
    
    async def scheduled_retrain(self):
        # Get high-quality examples
        examples = await self.feedback_store.get_examples(
            min_rating=0.9,
            limit=1000
        )
        
        if len(examples) < 50:
            logger.warning("Insufficient examples for retraining")
            return
        
        # Convert to DSPy examples
        train_set = [
            dspy.Example(
                question=ex["question"],
                answer=ex["answer"]
            ).with_inputs("question")
            for ex in examples
        ]
        
        # Retrain
        optimizer = dspy.BootstrapFewShot(metric=self.quality_metric)
        self.base_module = optimizer.compile(
            self.base_module,
            trainset=train_set
        )
        
        # Save new model
        await self.save_model_checkpoint()
        
        logger.info(f"Retrained with {len(train_set)} examples")
    
    def quality_metric(self, example, prediction):
        # Custom metric for optimization
        if len(prediction.answer) < 10:
            return 0.0
        
        # Check if answer addresses the question
        question_words = set(example.question.lower().split())
        answer_words = set(prediction.answer.lower().split())
        
        overlap = len(question_words.intersection(answer_words))
        relevance = overlap / len(question_words) if question_words else 0
        
        return min(1.0, relevance * 2)`,
      testCases: [
        {
          input: 'Continuous learning with feedback collection and retraining',
          expected: true,
          description: 'Complete continuous learning implementation',
        },
      ],
    },
  ],
  quiz: {
    id: 'dspy-m7-quiz',
    title: 'Module 7: Production Deployment - Knowledge Check',
    description: 'Test your understanding of DSPy production deployment',
    passingScore: 6,
    questions: [
      {
        id: 'q1',
        question:
          'What is the primary benefit of using thread-safe singleton pattern for DSPy services?',
        choices: [
          'Faster processing speed',
          'Ensures single initialization and resource efficiency',
          'Better error handling',
          'Improved accuracy',
        ],
        correctAnswer: 1,
        explanation:
          'Thread-safe singleton pattern ensures DSPy is initialized only once and resources are efficiently shared across threads.',
      },
      {
        id: 'q2',
        question: 'Which deployment option is best for serverless architectures?',
        choices: [
          'FastAPI with uvicorn',
          'MLflow deployment',
          'AWS Lambda with API Gateway',
          'Docker Compose',
        ],
        correctAnswer: 2,
        explanation:
          'AWS Lambda with API Gateway provides serverless deployment that scales automatically and charges only for usage.',
      },
      {
        id: 'q3',
        question: 'What is the purpose of implementing retry logic with exponential backoff?',
        choices: [
          'To improve model accuracy',
          'To handle transient failures and rate limits gracefully',
          'To reduce costs',
          'To speed up processing',
        ],
        correctAnswer: 1,
        explanation:
          'Exponential backoff helps handle transient failures and rate limits by progressively increasing wait times between retries.',
      },
      {
        id: 'q4',
        question: 'Why is structured logging important in production?',
        choices: [
          'It makes logs prettier',
          'It enables efficient log parsing and analysis',
          'It reduces storage costs',
          'It improves model performance',
        ],
        correctAnswer: 1,
        explanation:
          'Structured logging (like JSON format) enables efficient parsing, searching, and analysis of logs in production systems.',
      },
      {
        id: 'q5',
        question: 'What is the main purpose of implementing A/B testing for DSPy modules?',
        choices: [
          'To reduce costs',
          'To compare and validate different prompts or approaches',
          'To increase processing speed',
          'To improve security',
        ],
        correctAnswer: 1,
        explanation:
          'A/B testing allows you to compare different DSPy configurations or prompts to determine which performs better in production.',
      },
      {
        id: 'q6',
        question: 'Which monitoring metric is most important for cost management?',
        choices: ['Request latency', 'Error rate', 'Token usage', 'Active connections'],
        correctAnswer: 2,
        explanation:
          'Token usage directly correlates with API costs, making it the most important metric for cost management.',
      },
      {
        id: 'q7',
        question: 'What is the benefit of using vector databases with DSPy?',
        choices: [
          'Faster response times',
          'Semantic search over past interactions',
          'Lower costs',
          'Better error handling',
        ],
        correctAnswer: 1,
        explanation:
          'Vector databases enable semantic search over past DSPy interactions, useful for finding similar queries and responses.',
      },
      {
        id: 'q8',
        question: 'Why implement resource limits in Docker deployments?',
        choices: [
          'To make containers run faster',
          'To prevent resource exhaustion and ensure stability',
          'To reduce Docker image size',
          'To improve model accuracy',
        ],
        correctAnswer: 1,
        explanation:
          'Resource limits prevent containers from consuming excessive CPU/memory, ensuring system stability and predictable performance.',
      },
    ],
  },
};
